import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { getCheckoutStatus } from "@/lib/api";

const POLL_INTERVAL = 2000;
const MAX_POLLS = 15;

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | expired | error
  const [data, setData] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let active = true;

    const poll = async () => {
      try {
        const res = await getCheckoutStatus(sessionId);
        if (!active) return;
        setData(res);

        if (res.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (res.status === "expired") {
          setState("expired");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= MAX_POLLS) {
          setState("error");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (!active) return;
        attempts.current += 1;
        if (attempts.current >= MAX_POLLS) {
          setState("error");
        } else {
          setTimeout(poll, POLL_INTERVAL);
        }
      }
    };

    poll();
    return () => {
      active = false;
    };
  }, [sessionId]);

  return (
    <main
      data-testid="success-page"
      className="dark-section min-h-[100svh] flex items-center justify-center px-6 md:px-12"
    >
      <div className="max-w-2xl w-full text-center">
        {state === "checking" && (
          <>
            <p
              className="hairline opacity-60 mb-8"
              data-testid="success-checking"
            >
              Confirming…
            </p>
            <p className="font-serif italic text-2xl md:text-3xl opacity-80">
              A quiet moment, while the ledger settles.
            </p>
          </>
        )}

        {state === "paid" && (
          <>
            <p className="hairline opacity-70 mb-10" data-testid="success-label">
              Acquisition confirmed
            </p>
            <h1
              className="font-serif italic text-5xl md:text-7xl leading-[1.05]"
              data-testid="success-message"
            >
              You are now part of
              <br />
              Chapter II.
            </h1>
            {data?.artwork_title && (
              <div className="mt-10 space-y-2">
                <p
                  className="font-serif italic text-xl md:text-2xl opacity-80"
                  data-testid="success-artwork-title"
                >
                  {data.artwork_title}
                </p>
                {data.edition_name && (
                  <p
                    className="hairline opacity-60"
                    data-testid="success-edition"
                  >
                    {data.edition_code} · {data.edition_name}
                    {data.quantity > 1 ? ` · ×${data.quantity}` : ""}
                  </p>
                )}
              </div>
            )}
            <p className="hairline opacity-50 mt-16">
              Signed, numbered, and carefully packaged. A receipt has been sent
              to your email.
            </p>
            <div className="mt-16">
              <Link
                to="/"
                className="link-quiet font-serif italic text-lg"
                data-testid="success-return-link"
              >
                Return to the exhibition
              </Link>
            </div>
          </>
        )}

        {state === "expired" && (
          <>
            <p className="hairline opacity-60 mb-8">
              This moment has passed
            </p>
            <p
              className="font-serif italic text-3xl md:text-4xl"
              data-testid="success-expired"
            >
              The session has expired. You are welcome to return.
            </p>
            <div className="mt-16">
              <Link
                to="/#release"
                className="link-quiet font-serif italic text-lg"
                data-testid="success-retry-link"
              >
                Return to the release
              </Link>
            </div>
          </>
        )}

        {state === "error" && (
          <>
            <p className="hairline opacity-60 mb-8">A gentle pause</p>
            <p
              className="font-serif italic text-2xl md:text-3xl"
              data-testid="success-error"
            >
              We could not confirm just yet. Please check your email for
              confirmation, or return shortly.
            </p>
            <div className="mt-12">
              <Link
                to="/"
                className="link-quiet font-serif italic text-lg"
                data-testid="success-home-link"
              >
                Return home
              </Link>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
